import React, { useState, useRef, useEffect } from 'react';
import { t } from '../utils/translations';
import './SearchableCustomerSelect.css';

const SearchableCustomerSelect = ({ customers = [], value, onChange, onAddNew, required = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState(customers || []);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Filter customers based on search term
    const customersList = customers || [];
    if (searchTerm.trim() === '') {
      setFilteredCustomers(customersList);
    } else {
      const filtered = customersList.filter(customer =>
        customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer?.phone?.includes(searchTerm)
      );
      setFilteredCustomers(filtered);
    }
  }, [searchTerm, customers]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedCustomer = (customers || []).find(c => c?.id === value);

  const handleSelect = (customerId) => {
    if (onChange) {
      onChange(customerId);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleAddNew = () => {
    setIsOpen(false);
    setSearchTerm('');
    if (onAddNew) {
      onAddNew();
    }
  };

  return (
    <div className="searchable-select" ref={dropdownRef}>
      <div
        className="select-trigger"
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 0);
          }
        }}
      >
        <span className={selectedCustomer ? 'selected-value' : 'placeholder'}>
          {selectedCustomer ? selectedCustomer.name : t('Select Customer')}
        </span>
        <span className="arrow">{isOpen ? '▲' : '▼'}</span>
      </div>

      {isOpen && (
        <div className="select-dropdown">
          <div className="search-input-wrapper">
            <input
              ref={inputRef}
              type="text"
              className="search-input"
              placeholder={t('Search customers...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          
          <div className="dropdown-options">
            <div
              className="dropdown-option add-new-option"
              onClick={handleAddNew}
            >
              <span className="add-icon">+</span>
              <span>{t('Add New Customer')}</span>
            </div>
            
            {filteredCustomers.length === 0 ? (
              <div className="dropdown-option no-results">
                {t('No customers found')}
              </div>
            ) : (
              filteredCustomers.map(customer => {
                if (!customer || !customer.id) return null;
                return (
                  <div
                    key={customer.id}
                    className={`dropdown-option ${value === customer.id ? 'selected' : ''}`}
                    onClick={() => handleSelect(customer.id)}
                  >
                    <div className="customer-info">
                      <div className="customer-name">{customer.name || 'N/A'}</div>
                      {customer.email && (
                        <div className="customer-detail">{customer.email}</div>
                      )}
                      {customer.phone && (
                        <div className="customer-detail">{customer.phone}</div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableCustomerSelect;
